import { t } from '../../i18n/i18n'
import { Title } from '../../components/common/texts/Title'
import { Button, Divider, makeStyles, tokens } from '@fluentui/react-components'
import { useForm } from 'react-hook-form'
import { OrganisationDetailsForm } from './OrganisationDetailsForm'
import { OrganisationInvoiceForm } from './OrganisationInvoiceForm'
import { OrganisationModulesForm } from './OrganisationModulesForm'
import { UserAccountForm } from './UserAccountForm'
import { IModalState } from '../../interfaces/IModalState'
import { DataContext } from '../../App'
import { useContext, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { Loader } from '../../components/common/spinner/Loader'
import { saveOrganisation, upgradeSandboxToOrganisation } from '../../helpers/organisationHelper'
import { OrganisationWithUserAccountsDTO, SandboxOrganisationDTO } from '../../api/schemas/schema'
import { SandboxForm } from './SandboxForm'
type OrganisationFormProps = {
   modalState: IModalState<OrganisationWithUserAccountsDTO>
   setModalState: React.Dispatch<React.SetStateAction<IModalState<OrganisationWithUserAccountsDTO>>>
   onModalClose: () => void
   existingOrganisationNumbers: string[]
}
const useStyles = makeStyles({
   header: {
      zIndex: 3,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxSizing: 'border-box',
      height: '89px',
      backgroundColor: tokens.colorBrandBackground,
      boxShadow: tokens.shadow2,
      '& > *': {
         width: '33%',
      },
   },
   title: {
      color: '#FFDF9E',
      display: 'flex',
      justifyContent: 'center',
   },
   gridContainer: {
      display: 'flex',
      width: '80vw',
      margin: '0 auto',
   },
   leftSection: {
      padding: tokens.spacingHorizontalXXL,
      maxWidth: '48%',
   },
   divider: {
      marginTop: tokens.spacingHorizontalXXXL,
      marginBottom: tokens.spacingHorizontalXXXL,
   },
   rightSection: {
      height: '100%',
      padding: tokens.spacingHorizontalXXL,
      maxWidth: '48%',
   },
   grid: {
      marginTop: tokens.spacingVerticalM,
      padding: tokens.spacingHorizontalXXXL,
   },
   buttons: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: tokens.spacingHorizontalXXXL,
      gap: tokens.spacingHorizontalXL,
      '@media (max-width: 1007px)': {
         justifyContent: 'center',
      },
   },
})
export const OrganisationForm = ({ modalState, setModalState, onModalClose, existingOrganisationNumbers }: OrganisationFormProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { isLoading } = dataContext.state
   const { accounts } = useMsal()
   const [closeOnSubmit, setCloseOnSubmit] = useState<boolean>(false)
   const organisationForm = useForm<OrganisationWithUserAccountsDTO>({ defaultValues: modalState.item ?? {} })
   const {
      handleSubmit,
      reset,
      watch,
      formState: { isDirty, isSubmitting },
   } = organisationForm

   const onSubmit = (data: OrganisationWithUserAccountsDTO) => {
      data.organisation.isSandbox = false

      const currentUser = accounts[0]?.username
      data.organisation.modifiedBy = currentUser
      data.userAccounts.forEach((x) => (x.modifiedBy = currentUser))

      saveOrganisation(data, reset, dataContext, accounts, setModalState, setCloseOnSubmit, modalState?.item, closeOnSubmit && onModalClose)
   }

   const upgradeToOrganisation = (sandbox: SandboxOrganisationDTO) => {
      upgradeSandboxToOrganisation(sandbox, dataContext, setModalState, reset)
   }

   return (
      <>
         <div className={classes.header}>
            <Title as="h1" children={modalState.item && modalState.item.organisation.id ? t('EditCustomer') : t('NewCustomer')} className={classes.title} />
         </div>
         <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className={classes.gridContainer}>
               <div className={classes.leftSection}>
                  <OrganisationDetailsForm organisationForm={organisationForm} existingOrganisationNumbers={existingOrganisationNumbers} />
                  <OrganisationInvoiceForm organisationForm={organisationForm} />
               </div>

               <Divider appearance="brand" vertical className={classes.divider} />

               <div className={classes.rightSection}>
                  <OrganisationModulesForm organisationForm={organisationForm} />
                  <UserAccountForm organisationForm={organisationForm} modalState={modalState} />
                  {watch('organisation.isPartner') && <SandboxForm organisationForm={organisationForm} upgradeToOrganisation={upgradeToOrganisation} />}

                  <div className={classes.buttons}>
                     <Button appearance="secondary" onClick={() => onModalClose()} disabled={isLoading}>
                        {t('Cancel')}
                     </Button>
                     <Button disabled={!isDirty || isSubmitting || isLoading} appearance="secondary" type="submit" onClick={() => setCloseOnSubmit(true)}>
                        {t('SaveAndClose')}
                     </Button>
                     <Button disabled={!isDirty || isSubmitting || isLoading} appearance="primary" type="submit">
                        {t('Save')}
                     </Button>
                  </div>
               </div>
               {isLoading && <Loader transparent />}
            </div>
         </form>
      </>
   )
}
